// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import { CartWidget } from '@gandi/cart';

import type { FunnelControlsProps } from 'src/Layout/components/FunnelControls/FunnelControls';
import { ShopLayout } from 'src/Layout/containers/ShopLayout';
import { usePreferences } from 'src/Layout/hooks';
import { ReshapedGandiProvider } from 'src/Reshaped/ReshapedGandiProvider';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { setPreferencesDialogOpen } from 'src/modules/app/actions';
import { selectPreferencesDialogOpen } from 'src/modules/app/selectors/layout';
import { selectAdminURL } from 'src/modules/config';
import type { FullState, PreFetchArgs, PreFetchReturn } from 'src/types';

import LayoutContext from './LayoutContext';
import {
  getIsCartOverviewPage,
  getIsCheckoutPage,
} from './helpers/pageMatchers';

type Props = {
  children: React.Node,
  lockedOrga?: boolean,
  showCartSummary?: boolean,
  backgroundColor?: 'surface',
  /**
   * 1. Address
   * 2. Summary
   * 3. Payment
   * 4. Payment failed
   * 5. Confirmation (payment return success) – no breadcrumbs or menu displayed
   */
  currentStep?: FunnelControlsProps['currentStep'],

  /** Optional secondary header */
  header?: React.Node,
};

export function Layout({
  backgroundColor,
  showCartSummary,
  children,
  lockedOrga,
  currentStep,
  header,
}: Props): React.Node {
  const location = useLocation();
  const dashboardURL = useAppSelector(selectAdminURL);

  const open = useAppSelector(selectPreferencesDialogOpen);
  const dispatch = useDispatch();

  const preferences = usePreferences();

  const preferencesDialog = {
    open,
    close: () => dispatch(setPreferencesDialogOpen(false)),
    show: () => dispatch(setPreferencesDialogOpen(true)),
  };

  const contextValue = React.useMemo(
    () => ({ locked: !!lockedOrga }),
    [lockedOrga],
  );

  const isCartOverviewPage = getIsCartOverviewPage(location);

  const isCheckoutPage = getIsCheckoutPage(location);

  return (
    <ReshapedGandiProvider>
      <LayoutContext.Provider value={contextValue}>
        <ShopLayout
          backgroundColor={backgroundColor}
          showCartSummary={showCartSummary}
          widgets={
            <div>
              {/** $FlowFixMe[prop-missing] countyCode is missing in lib/cart */}
              <CartWidget
                noSuggestions={isCartOverviewPage}
                noCheckoutButton={isCheckoutPage}
                bypassRetrieveCart={isCheckoutPage}
                // $FlowFixMe[incompatible-type]
                openPreferences={preferencesDialog.show}
                preferences={preferences}
              />
            </div>
          }
          header={header}
          dashboardURL={dashboardURL}
          // $FlowFixMe[incompatible-type]
          preferencesDialog={preferencesDialog}
          currentStep={currentStep}
        >
          {children}
        </ShopLayout>
      </LayoutContext.Provider>
    </ReshapedGandiProvider>
  );
}

Layout.preFetch = function preFetch(
  args: PreFetchArgs<{}, any, any, FullState>,
): PreFetchReturn<> {
  return ShopLayout.preFetch(args);
};

Layout.handleRedirect = function handleRedirect(
  args: StaticHandlerArgs<>,
): Promise<void> | Promise<mixed> {
  return ShopLayout.handleRedirect(args);
};
